import React from 'react';
import { Box, Typography, Grid, Card, CardContent, LinearProgress } from '@mui/material';
import { BRAND } from '../../config/brand';
import { useGetLocationsQuery, useGetMovementsQuery } from '../../api/warehouseApi';
import EntityDataTable, { EntityColumn } from '../common/EntityDataTable';

export const WarehouseManagement: React.FC = () => {
  const { data: locations = [], isLoading: locLoading } = useGetLocationsQuery();
  const { data: movements = [], isLoading: movLoading } = useGetMovementsQuery();

  const locationColumns: EntityColumn<Record<string, any>>[] = [
    { id: 'code', label: 'Code' },
    { id: 'zone', label: 'Zone' },
    { id: 'aisle', label: 'Aisle' },
    { id: 'shelf', label: 'Shelf' },
    {
      id: 'occupancy',
      label: 'Occupancy',
      render: (row) => (
        <Box sx={{ minWidth: 120 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (row.currentOccupancy / row.capacity) * 100)}
            sx={{ height: 8, borderRadius: 4, mb: 0.5, '& .MuiLinearProgress-bar': { bgcolor: BRAND.colors.navy } }}
          />
          {row.currentOccupancy}/{row.capacity}
        </Box>
      ),
    },
  ];

  const movementColumns: EntityColumn<Record<string, any>>[] = [
    { id: 'type', label: 'Type' },
    { id: 'quantity', label: 'Qty' },
    { id: 'fromLocation', label: 'From' },
    { id: 'toLocation', label: 'To' },
    { id: 'reference', label: 'Reference' },
    {
      id: 'createdAt',
      label: 'Date',
      render: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ color: BRAND.colors.navy, fontWeight: 700, mb: 3 }}>
        Warehouse Management
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(locations as any[]).map((loc) => (
          <Grid item xs={12} sm={6} md={4} key={loc.id}>
            <Card sx={{ borderLeft: `4px solid ${BRAND.colors.navy}` }}>
              <CardContent>
                <Typography variant="h6">{loc.code}</Typography>
                <Typography variant="caption">Zone {loc.zone} · Aisle {loc.aisle} · Shelf {loc.shelf}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{loc.productType} — {loc.status}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <EntityDataTable
        title="Warehouse Locations"
        data={locations as any[]}
        columns={locationColumns}
        statusField="status"
        searchFields={['code', 'zone']}
        loading={locLoading}
      />

      <Box sx={{ mt: 4 }}>
        <EntityDataTable
          title="Stock Movements"
          data={movements as any[]}
          columns={movementColumns}
          statusField="type"
          searchFields={['reference', 'type']}
          loading={movLoading}
        />
      </Box>
    </Box>
  );
};

export default WarehouseManagement;
